import { readCookie, readCookiesWhere, writeCookie } from './lib/browser-cookies';
import {
    canReadLegacyMirrors,
    consentExpiresAt,
    getLegacyMirrorCookieName,
    getMirrorCookieName,
    getParentCookieDomain,
    isLegacyMirrorCookieName,
    isNewerConsent,
    isCurrentDecision,
    newestCurrentDecision,
    type ConsentScope,
} from './lib/consent-value';

/**
 * Carries a Cookiebot consent decision across subdomains (SDUI-1442).
 *
 * Cookiebot writes `CookieConsent` host-only. The explicit accept/decline write comes from
 * the script its server returns for `logconsent.ashx`, which passes the domain argument as
 * an empty string in the observed response. Its own carry-over feature,
 * Cross-domain Consent Sharing, is third-party localStorage on `consentcdn.cookiebot.com`
 * whose `registerBulkConsent` is gated on `this.consent.preferences`. A decline sets that
 * false, so a decline is never shared, and browsers that block third-party storage share
 * nothing at all. Result: decline on `www.clearscore.com`, land on `app.clearscore.com`, and
 * you are asked a second time.
 *
 * This module makes the decision first-party instead:
 *  - `mirrorCookieConsent` copies whatever Cookiebot recorded on this host up to a cookie
 *    on the parent domain, on every decision.
 *  - `seedCookieConsent` copies it back down onto the current host before `uc.js` loads,
 *    so Cookiebot boots already holding the answer.
 *
 * Verified against production: Cookiebot honours a value minted on a different host
 * verbatim, keeps the original stamp, builds no banner and fires no `logconsent.ashx`, and
 * a seeded decline survives the real www-to-app journey. So this also removes the duplicate
 * consent-log entry the accept path creates today.
 *
 * Only an explicit, recorded decision is ever carried, byte for byte. Cookiebot's sentinels
 * (`-1`, `-2`, `-3`, `0`) and its implied out-of-region value are never copied, and every
 * write uses the original timestamp and our configured lifetime rather than restarting it.
 * Keep that lifetime aligned with the Cookiebot configuration.
 *
 * A withdrawal needs no special handling: Cookiebot's server records it as an explicit
 * decline with a fresh timestamp (`logconsent.ashx?action=decline` returns a `setCookie`
 * of a decline-shaped value), so it flows through the same path as any other decision and
 * replaces the older acceptance on every host.
 *
 * The mirror is keyed by brand, not by Cookiebot domain group (SDUI-1657). A brand can load
 * more than one group (the GB banner A/B test serves each arm as its own group), and the
 * decision has to follow the visitor across all of them, or the second host asks again. The
 * value Cookiebot records carries no group id, so it is safe to replay under any group of
 * the same brand. Mirrors written under the old group-keyed name are still read, so nobody
 * who decided before the change is re-prompted. The old name is also still written, for the
 * group a host loads, because hosts still running the previous code read nothing else and
 * would otherwise miss a decision made here (a withdrawal included) for as long as the
 * deploy is staggered or rolled back. Both fallbacks go once every surface reads the brand
 * mirror and the old names have lapsed.
 *
 * Deliberately no hook on `CookiebotOnDialogDisplay`. Cookiebot's `show()` drops
 * `hasResponse` to false before it displays the dialog, and `renew()` (behind every
 * "Change your consent" link) is `show(true)`, so "dialog shown with no response" is
 * indistinguishable from a visitor asking to change their mind. Re-applying the stored
 * decision there would silently answer that dialog for them.
 */

/** Cookiebot's own cookie. Host-only, written by the script `logconsent.ashx` returns. */
export const CONSENT_COOKIE = 'CookieConsent';

const MIRROR_EVENTS = ['CookiebotOnAccept', 'CookiebotOnDecline', 'CookiebotOnConsentReady'] as const;

interface CookiebotApi {
    hasResponse?: boolean;
}

// Via unknown rather than intersecting with Window: other packages declare their own global
// Window.Cookiebot shapes, and this module only relies on the one member above.
const getCookiebot = (): CookiebotApi | undefined => (window as unknown as { Cookiebot?: CookiebotApi }).Cookiebot;

const getShareDomain = (): string | null => getParentCookieDomain(window.location.hostname);

/**
 * The newest decision shared on the parent domain for this brand: the brand-keyed mirror,
 * folded with the group-keyed mirrors from before SDUI-1657 that can be attributed to it.
 * That is always this deployment's own group, and every group where the parent domain is
 * this brand's alone.
 */
const readMirroredConsent = (scope: ConsentScope, legacyCookiebotId?: string): string | undefined => {
    const ownLegacyName = getLegacyMirrorCookieName(legacyCookiebotId);
    const legacy = canReadLegacyMirrors(window.location.hostname, scope)
        ? readCookiesWhere(isLegacyMirrorCookieName)
        : [];

    return newestCurrentDecision(
        readCookie(getMirrorCookieName(scope)),
        ownLegacyName ? readCookie(ownLegacyName) : null,
        ...legacy,
    );
};

/**
 * Copies this host's recorded decision to the parent domain.
 *
 * `legacyCookiebotId` is the domain group this deployment is configured with. The decision is
 * also written under that group's old name, which is the only one the previous code reads.
 *
 * Returns whether there was a decision to act on, so the caller can retry if Cookiebot has
 * not written its cookie yet.
 */
export const mirrorCookieConsent = (scope: ConsentScope, legacyCookiebotId?: string): boolean => {
    if (typeof window === 'undefined') return false;

    const shareDomain = getShareDomain();
    if (!shareDomain) return false;

    // Cookiebot fires CookiebotOnDecline at boot for a visitor who has never answered, and
    // show()/renew() also drop hasResponse while a dialog is open. Neither is a decision, so
    // there is nothing to carry and nothing already carried should be touched.
    if (getCookiebot()?.hasResponse !== true) return true;

    const hostConsent = readCookie(CONSENT_COOKIE);
    if (!hostConsent) return false;

    // Sentinels and implied values are Cookiebot's guesses about this host, not choices.
    if (!isCurrentDecision(hostConsent)) return true;

    const expires = consentExpiresAt(hostConsent);
    if (!expires) return true;

    // Each copy is checked on its own, and only ever replaced by a strictly newer decision,
    // so a more recent choice made on another host is never overwritten.
    const carry = (name: string): void => {
        const mirrored = readCookie(name);
        if (isCurrentDecision(mirrored) && !isNewerConsent(hostConsent, mirrored)) return;
        writeCookie(name, hostConsent, { domain: shareDomain, expires });
    };

    carry(getMirrorCookieName(scope));

    const legacyName = getLegacyMirrorCookieName(legacyCookiebotId);
    if (legacyName) carry(legacyName);

    return true;
};

/**
 * Copies the most recent shared decision onto this host, so Cookiebot finds it at boot.
 * Must run before `uc.js` is injected.
 *
 * Last write wins, so a change of mind on `app` propagates back to `www` rather than only
 * forward. A recorded decision always replaces a sentinel, because the visitor explicitly
 * chose one and Cookiebot only guessed the other.
 */
export const seedCookieConsent = (scope: ConsentScope, legacyCookiebotId?: string): void => {
    if (typeof window === 'undefined') return;
    if (!getShareDomain()) return;

    const mirrored = readMirroredConsent(scope, legacyCookiebotId);
    if (mirrored === undefined) return;

    const hostConsent = readCookie(CONSENT_COOKIE);
    if (hostConsent === mirrored) return;
    if (isCurrentDecision(hostConsent) && !isNewerConsent(mirrored, hostConsent)) return;

    const expires = consentExpiresAt(mirrored);
    if (!expires) return;

    // Host-only, matching the scope Cookiebot writes at, so only ever one such cookie exists.
    writeCookie(CONSENT_COOKIE, mirrored, { expires });
};

const handlers = new Map<string, () => void>();

/**
 * Mirrors the decision whenever Cookiebot reports one.
 *
 * Cookiebot fires these events from `init()`, which runs in the callback after the script
 * from `logconsent.ashx` has executed and written the cookie, so the value is normally
 * there already. The single retry covers the ordering not holding.
 */
export const startCookieConsentMirror = (scope: ConsentScope, legacyCookiebotId?: string): void => {
    if (typeof window === 'undefined' || handlers.has(scope)) return;

    const mirror = (): void => {
        if (!mirrorCookieConsent(scope, legacyCookiebotId)) {
            window.setTimeout(() => mirrorCookieConsent(scope, legacyCookiebotId), 0);
        }
    };

    handlers.set(scope, mirror);
    MIRROR_EVENTS.forEach((eventName) => window.addEventListener(eventName, mirror, false));
};

/**
 * Call immediately before injecting `uc.js`, with the brand whose banner it will show and the
 * domain group id the deployment is configured with.
 */
export const initCookieConsentSync = (scope: ConsentScope, legacyCookiebotId?: string): void => {
    seedCookieConsent(scope, legacyCookiebotId);
    startCookieConsentMirror(scope, legacyCookiebotId);
};

/** Test seam: detaches every listener and forgets which brands had them. */
export const resetCookieConsentSyncForTests = (): void => {
    if (typeof window !== 'undefined') {
        handlers.forEach((handler) => {
            MIRROR_EVENTS.forEach((eventName) => window.removeEventListener(eventName, handler, false));
        });
    }
    handlers.clear();
};
